/**
 * Abstract base class for wallet providers
 * 
 * This defines the interface that all wallet providers must implement,
 * enabling easy extension to support additional wallet types like
 * WalletConnect, Coinbase Wallet, etc.
 */
export class WalletProvider {
  constructor() {
    if (this.constructor === WalletProvider) {
      throw new Error('WalletProvider is abstract and cannot be instantiated directly');
    }
  }

  /**
   * Get the wallet type identifier
   * @returns {string} Wallet type (e.g., 'metamask', 'privateKey', 'walletConnect')
   */
  getType() {
    throw new Error('getType() must be implemented by wallet provider');
  }

  /**
   * Check if this wallet provider is available/supported
   * @returns {boolean} True if wallet is available
   */
  isAvailable() {
    throw new Error('isAvailable() must be implemented by wallet provider');
  }

  /**
   * Initialize the wallet provider
   * @returns {Promise<void>}
   */
  async initialize() {
    throw new Error('initialize() must be implemented by wallet provider');
  }

  /**
   * Connect to the wallet and request account access
   * @returns {Promise<void>}
   */
  async connect() {
    throw new Error('connect() must be implemented by wallet provider');
  }

  /**
   * Disconnect from the wallet
   * @returns {Promise<void>}
   */
  async disconnect() {
    throw new Error('disconnect() must be implemented by wallet provider');
  }

  /**
   * Get the Ethers.js provider instance
   * @returns {ethers.providers.Provider} Ethers provider
   */
  getProvider() {
    throw new Error('getProvider() must be implemented by wallet provider');
  }

  /**
   * Get the Ethers.js signer instance
   * @returns {ethers.Signer} Ethers signer
   */
  getSigner() {
    throw new Error('getSigner() must be implemented by wallet provider');
  }

  /**
   * Get the current connected accounts
   * @returns {Promise<string[]>} Array of account addresses
   */
  async getAccounts() {
    throw new Error('getAccounts() must be implemented by wallet provider');
  }

  /**
   * Get the current network information
   * @returns {Promise<Object>} Network info {chainId, name}
   */
  async getNetwork() {
    throw new Error('getNetwork() must be implemented by wallet provider');
  }

  /**
   * Check if the wallet is currently connected
   * @returns {boolean} True if connected
   */
  isConnected() {
    throw new Error('isConnected() must be implemented by wallet provider');
  }

  /**
   * Get wallet-specific configuration for event handling
   * @returns {Object} Event handling configuration
   */
  getEventConfig() {
    return {
      // Default configuration - providers can override
      fallbackInterval: 5000,
      connectionTimeout: 30000,
      supportsRealTimeEvents: true
    };
  }

  /**
   * Get a human-readable name for this wallet type
   * @returns {string} Display name
   */
  getDisplayName() {
    throw new Error('getDisplayName() must be implemented by wallet provider');
  }

  /**
   * Get any error message from the last operation
   * @returns {string} Error message or empty string
   */
  getErrorMessage() {
    return '';
  }

  /**
   * Create a contract instance with this provider
   * @param {string} address Contract address
   * @param {Array} abi Contract ABI
   * @param {boolean} needsSigner Whether contract needs signer for transactions
   * @returns {ethers.Contract} Contract instance
   */
  createContract(address, abi, needsSigner = false) {
    // Only check connection for transactions that need a signer
    if (needsSigner && !this.isConnected()) {
      throw new Error('Wallet not connected. Please connect your wallet first.');
    }

    const contractProvider = needsSigner && this.getSigner() ? this.getSigner() : this.getProvider();
    if (!contractProvider) {
      if (needsSigner) {
        throw new Error('Wallet signer not available. Please connect your wallet first.');
      } else {
        throw new Error('Provider not available for reading blockchain data. Wallet may not be initialized.');
      }
    }
    
    const ethers = require('ethers');
    return new ethers.Contract(address, abi, contractProvider);
  }

  /**
   * Event handlers for wallet events (optional)
   */
  onAccountsChanged(callback) {
    // Override in provider implementations that support this
  }

  onChainChanged(callback) {
    // Override in provider implementations that support this
  }

  onConnect(callback) {
    // Override in provider implementations that support this
  }

  onDisconnect(callback) {
    // Override in provider implementations that support this
  }
}

// Wallet type constants
export const WALLET_TYPES = {
  METAMASK: 'metamask',
  CUSTOM_RPC: 'customRPC',
  // Future wallet types can be added here:
  // WALLET_CONNECT: 'walletConnect',
  // COINBASE: 'coinbase',
  // LEDGER: 'ledger'
};

export default WalletProvider;